import React, { useState } from 'react';
import { HelpCircle, ChevronDown, MessageCircle } from 'lucide-react';

const translations = {
  de: {
    title: 'F.A.Q.',
    subtitle: 'Häufig gestellte Fragen',
    faqs: [
      {
        question: 'Wie kann ich FitXcel nutzen?',
        answer: 'Nach der Anmeldung erhalten Sie sofortigen Zugang zu Ihrem individuellen Trainings- und Ernaehrungsprogramm. Sie koennen direkt starten.',
      },
      {
        question: 'Was ist in FitXcel enthalten?',
        answer: 'Sie erhalten AI-gestuetzte Trainingsplaene, einen ausgewogenen Essensplan und eine Vielzahl an Rezepten fuer verschiedene Ernaehrungsformen.',
      },
      {
        question: 'Wie lange habe ich Zugriff?',
        answer: 'Der Zugang fuer 4 Wochen kostet einmalig 15 Euro (inkl. MwSt.).',
      },
      {
        question: 'Funktioniert FitXcel auf dem Smartphone?',
        answer: 'Ja. Sie koennen FitXcel auf Smartphone, Tablet und Desktop nutzen.',
      },
      {
        question: 'Ist FitXcel fuer Anfaenger geeignet?',
        answer: 'Ja. Das Programm ist fuer Einsteiger und Fortgeschrittene geeignet und passt sich Ihrem Niveau an.',
      },
      {
        question: 'Gibt es vegetarische oder vegane Optionen?',
        answer: 'Ja. Die AI beruecksichtigt Veganer, Vegetarier und Allesesser bei der Rezept- und Essensplanung.',
      },
      {
        question: 'Wie kann ich kuendigen?',
        answer: 'Die Kuendigung kann per Telefon unter 08005895405 oder per E-Mail an fitxcel.de@silverlines.info erfolgen.',
      },
      {
        question: 'Wo finde ich rechtliche Informationen?',
        answer: 'Alle Informationen finden Sie unter AGB, Impressum, Kontakt und Datenschutz im Footer.',
      },
    ],
  },
  en: {
    title: 'F.A.Q.',
    subtitle: 'Frequently Asked Questions',
    faqs: [
      {
        question: 'How can I use FitXcel?',
        answer: 'After signup, you get immediate access to your personalized training and nutrition program.',
      },
      {
        question: 'What is included in FitXcel?',
        answer: 'You get AI-powered training plans, a balanced meal plan and many recipes for different nutrition preferences.',
      },
      {
        question: 'How long is the access period?',
        answer: 'Access for 4 weeks is a one-time payment of 15 EUR (incl. VAT).',
      },
      {
        question: 'Does FitXcel work on mobile?',
        answer: 'Yes. You can use FitXcel on smartphone, tablet and desktop.',
      },
      {
        question: 'Is FitXcel suitable for beginners?',
        answer: 'Yes. The program is built for both beginners and advanced users and adapts to your level.',
      },
      {
        question: 'Are vegetarian and vegan options included?',
        answer: 'Yes. The AI supports vegan, vegetarian and omnivore meal preferences.',
      },
      {
        question: 'How can I cancel?',
        answer: 'Cancellation can be made by phone at 08005895405 or by email at fitxcel.de@silverlines.info.',
      },
      {
        question: 'Where can I find legal information?',
        answer: 'All legal information is available in Terms, Imprint, Contact and Privacy links in the footer.',
      },
    ],
  }
};

function FAQ({ language }) {
  const t = translations[language];
  const [openIndex, setOpenIndex] = useState(null);

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section id="faq" className="relative py-24 overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-secondary/50 to-transparent"></div>
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-secondary/10 border border-secondary/30 rounded-full mb-6">
            <HelpCircle className="w-5 h-5 text-secondary" />
            <span className="text-secondary text-sm font-body font-semibold tracking-wide">{t.subtitle}</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-white mb-4">
            {t.title}
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-primary via-secondary to-accent mx-auto rounded-full"></div>
        </div>

        {/* FAQ Accordion */}
        <div className="space-y-4">
          {t.faqs.map((faq, index) => (
            <div
              key={index}
              className={`relative rounded-xl overflow-hidden transition-all duration-300 ${
                openIndex === index 
                  ? 'bg-gradient-to-r from-primary/10 to-secondary/10 border border-primary/30' 
                  : 'bg-surface/50 border border-slate-700 hover:border-slate-600'
              }`}
            >
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full flex items-center justify-between p-6 text-left"
              >
                <div className="flex items-center gap-4">
                  <span className="flex-shrink-0 w-8 h-8 bg-gradient-to-br from-primary to-secondary rounded-lg flex items-center justify-center text-white font-heading font-bold text-sm">
                    {index + 1}
                  </span>
                  <span className="font-body font-semibold text-lg text-white pr-4">
                    {faq.question}
                  </span>
                </div>
                <ChevronDown 
                  className={`w-6 h-6 text-slate-400 flex-shrink-0 transition-transform duration-300 ${
                    openIndex === index ? 'rotate-180 text-accent' : ''
                  }`}
                />
              </button>
              
              <div 
                className={`overflow-hidden transition-all duration-300 ${
                  openIndex === index ? 'max-h-96' : 'max-h-0'
                }`}
              >
                <div className="px-6 pb-6 pl-[4.5rem]">
                  <p className="text-slate-400 font-body text-base leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Contact prompt */}
        <div className="mt-12 text-center">
          <div className="inline-flex items-center gap-3 px-6 py-3 bg-surface/50 border border-slate-700 rounded-full">
            <MessageCircle className="w-5 h-5 text-accent" />
            <span className="text-slate-400 font-body">
              {language === 'de' 
                ? 'Haben Sie weitere Fragen? Kontaktieren Sie uns!' 
                : 'Have more questions? Contact us!'}
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}

export default FAQ;


